import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../config/api';

// Types
interface Place {
  id: string;
  title: string;
  address: string;
  total_score: number;
  reviews_count: number;
  [key: string]: any;
}

interface Review {
  id: string;
  text: string;
  stars: number;
  reviewer_name: string;
  published_at: string;
  detailed_ratings?: { category: string; rating: number }[];
  review_images?: string[];
  [key: string]: any;
}

interface Analytics {
  overall_stats: {
    total_reviews: number;
    average_rating: number;
    five_star_count: number;
    four_star_count: number;
    three_star_count: number;
    two_star_count: number;
    one_star_count: number;
  };
  review_trend: {
    month: string;
    review_count: number;
    average_rating: number;
  }[];
  detailed_ratings: {
    category: string;
    average_rating: number;
    rating_count: number;
  }[];
}

interface GoogleReviewsState {
  places: Place[];
  selectedPlace: Place | null;
  placeReviews: Review[];
  placeAnalytics: Analytics | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: GoogleReviewsState = {
  places: [],
  selectedPlace: null,
  placeReviews: [],
  placeAnalytics: null,
  loading: false,
  error: null,
};

// Thunks
export const fetchPlaces = createAsyncThunk(
  'googleReviews/fetchPlaces',
  async (brandId?: string) => {
    try {
      const params = brandId ? { brand_id: brandId } : {};
      const response = await api.get('/google-reviews/places', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

export const fetchPlaceWithReviews = createAsyncThunk(
  'googleReviews/fetchPlaceWithReviews',
  async (placeId: string) => {
    const response = await api.get(`/google-reviews/places/${placeId}`);
    return response.data;
  }
);

export const fetchPlaceAnalytics = createAsyncThunk(
  'googleReviews/fetchPlaceAnalytics',
  async (placeId: string) => {
    const response = await api.get(`/google-reviews/places/${placeId}/analytics`);
    return response.data;
  }
);

export const fetchAllPlacesAnalytics = createAsyncThunk(
  'googleReviews/fetchAllPlacesAnalytics',
  async (brandId?: string) => {
    const params = brandId ? { brand_id: brandId } : {};
    const response = await api.get('/google-reviews/places-analytics', { params });
    return response.data;
  }
);

export const addPlace = createAsyncThunk(
  'googleReviews/addPlace',
  async (placeData: Partial<Place>) => {
    const response = await api.post('/google-reviews/places', placeData);
    return response.data;
  }
);

export const addReview = createAsyncThunk(
  'googleReviews/addReview',
  async (reviewData: Partial<Review>) => {
    const response = await api.post('/google-reviews/reviews', reviewData);
    return response.data;
  }
);

// Slice
const googleReviewsSlice = createSlice({
  name: 'googleReviews',
  initialState,
  reducers: {
    clearSelectedPlace: (state) => {
      state.selectedPlace = null;
      state.placeReviews = [];
      state.placeAnalytics = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch places
      .addCase(fetchPlaces.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlaces.fulfilled, (state, action) => {
        state.loading = false;
        state.places = action.payload;
      })
      .addCase(fetchPlaces.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch places';
      })

      // Fetch place with reviews
      .addCase(fetchPlaceWithReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlaceWithReviews.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPlace = action.payload.place;
        state.placeReviews = action.payload.reviews;
      })
      .addCase(fetchPlaceWithReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch place details';
      })

      // Fetch place analytics
      .addCase(fetchPlaceAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlaceAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.placeAnalytics = action.payload;
      })
      .addCase(fetchPlaceAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch analytics';
      })

      // Fetch all places analytics
      .addCase(fetchAllPlacesAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllPlacesAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.placeAnalytics = action.payload;
      })
      .addCase(fetchAllPlacesAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch all places analytics';
      })

      // Add place
      .addCase(addPlace.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addPlace.fulfilled, (state, action) => {
        state.loading = false;
        state.places.push(action.payload);
      })
      .addCase(addPlace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to add place';
      })

      // Add review
      .addCase(addReview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addReview.fulfilled, (state, action) => {
        state.loading = false;
        state.placeReviews.unshift(action.payload);
      })
      .addCase(addReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to add review';
      });
  },
});

export const { clearSelectedPlace, clearError } = googleReviewsSlice.actions;
export default googleReviewsSlice.reducer; 